import { Building2, MapPin, MapPinOff, Tablet } from 'lucide-react';
import { formatDistance, GEO_LABELS, needsReview, reviewChips, type GeoStatus, type TimeEntry } from '@tradeoutlet/shared';
import clsx from 'clsx';

const TONE: Record<GeoStatus, string> = {
  inside: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  outside: 'bg-orange-50 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  unknown: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  kiosk: 'bg-brand-yellow/20 text-ink dark:text-slate-100',
  not_checked: '',
};

/** Where a clock event happened. "Not checked" (no fence) shows nothing. */
export function GeoBadge({ geo, distance, label }: { geo?: GeoStatus | null; distance?: number | null; label?: string }) {
  if (!geo || geo === 'not_checked') return null;
  const Icon = geo === 'inside' ? MapPin : geo === 'kiosk' ? Tablet : geo === 'outside' ? MapPinOff : Building2;
  const text = geo === 'outside' && distance != null ? `${GEO_LABELS.outside} · ${formatDistance(distance)}` : GEO_LABELS[geo];
  return (
    <span className={clsx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium', TONE[geo])} title={label ? `${label}: ${text}` : text} data-geo={geo}>
      <Icon className="h-3 w-3" />{label && <span className="opacity-70">{label}</span>}{text}
    </span>
  );
}

/** In / out badges for a timesheet row, plus the kiosk name. */
export function EntryGeo({ e }: { e: TimeEntry }) {
  const inG = e.clock_in_geo, outG = e.clock_out_geo;
  if ((!inG || inG === 'not_checked') && (!outG || outG === 'not_checked')) return null;
  if (inG === 'kiosk' && (outG === 'kiosk' || !outG)) {
    return <span className="inline-flex flex-wrap gap-1"><GeoBadge geo="kiosk" />{e.kiosk_name && <span className="text-[11px] text-slate-500">{e.kiosk_name}</span>}</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      <GeoBadge geo={inG} distance={e.clock_in_distance_m} label="In" />
      <GeoBadge geo={outG} distance={e.clock_out_distance_m} label="Out" />
    </span>
  );
}

/** t187u: why an entry needs a closer look (offline punch sent late, phone clock off, outside the fence…). */
export function ReviewChips({ e }: { e: TimeEntry }) {
  const chips = needsReview(e) ? reviewChips(e) : [];
  if (!chips.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="review-chips">
      {chips.map(c => <span key={c} className="text-[11px] font-medium text-orange-900 dark:text-orange-200">{c}</span>)}
    </span>
  );
}
